import { useQuery } from "convex/react";
import { api } from "$convex/convexApi";
import { useAuthIdentity } from "@/contexts/UserProfileContext";

// Map frontend tab values to backend contractStatus values
const STATUS_TO_BACKEND: Record<string, string> = {
  pending: "pending_approval",
};

// Map backend contractStatus values to frontend display values
const STATUS_TO_FRONTEND: Record<string, string> = {
  pending_approval: "pending",
};

function normalizeContracts(raw: any[] | undefined) {
  if (!raw) return undefined;
  return raw.map((c) => {
    const rawStatus = c.contractStatus ?? c.status;
    return {
      ...c,
      status: STATUS_TO_FRONTEND[rawStatus] ?? rawStatus,
      customerName:
        c.customerName ?? c.user?.company ?? c.user?.name ?? c.user?.email,
      volumeCommittedBags:
        c.volumeCommittedBags ??
        (Array.isArray(c.items)
          ? c.items.reduce(
              (sum: number, i: any) => sum + (i.totalBags ?? i.quantity ?? 0),
              0,
            )
          : undefined),
      totalValue: c.totalValue ?? c.totalAmount,
    };
  });
}

export function useContractsByStatus(
  status?:
    | "all"
    | "draft"
    | "pending"
    | "approved"
    | "signed"
    | "active"
    | "completed"
    | "cancelled"
) {
  const { authUserId } = useAuthIdentity();
  const backendStatus =
    status && status !== "all"
      ? STATUS_TO_BACKEND[status] ?? status
      : undefined;
  const raw = useQuery(
    api.contractWorkflow.getContractsByStatus,
    authUserId
      ? { authUserId, ...(backendStatus ? { status: backendStatus } : {}) }
      : "skip"
  );
  return normalizeContracts(raw);
}

export function useContract(contractId?: string) {
  return useQuery(
    api.contractWorkflow.getContract,
    contractId ? { contractId } : "skip"
  );
}

export function useSuggestedContractPrices(
  customerId?: string,
  cargoIds?: string[],
) {
  const uniqueCargoIds = Array.from(new Set((cargoIds ?? []).filter(Boolean)));

  return useQuery(
    api.contractWorkflow.getSuggestedDraftContractPrices,
    customerId && uniqueCargoIds.length > 0
      ? { customerId: customerId as any, cargoIds: uniqueCargoIds as any }
      : "skip",
  );
}
